import * as Api from '@ViewModels';
import { css, type StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import { useState } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	useEventAutomationCoverage,
	useGetSurveyById,
	useStartEventAutomationMutation,
} from '../../../../queries/Surveys';
import { Button } from '../../../../web/components-v2/Button';
import { describeStartOutcome, getCoverageCopy, getCoverageState, getStartableContactIds } from './coverageState';
import { EventAutomationCoverageDialog } from './EventAutomationCoverageDialog';
import { StartEventAutomationDialog } from './StartEventAutomationDialog';
import { coverageToneStyles, coverageWarningStyles } from './styles';

interface EventAutomationCoverageWarningProps {
	surveyId?: string;
	automationTemplate?: Api.IAutomationTemplate;
	validResponses?: Api.EventRegistrationResponseStatus[];
	impersonationContext?: Api.IImpersonationContext;
	variant: 'banner' | 'inline';
	/** Offers starting the automation for the missed registrants; only the live automation page sets this. */
	allowStart?: boolean;
	styles?: StyleDeclarationValue[];
}

export function getPublishedEventId(template?: Api.IAutomationTemplate) {
	const trigger = template?.published?.trigger;
	return trigger?._type === Api.AutomationTriggerType.EventRegistration
		? (trigger as Api.IEventRegistrationTrigger).eventId
		: undefined;
}

function getPublishedValidResponses(template?: Api.IAutomationTemplate) {
	const trigger = template?.published?.trigger;
	return trigger?._type === Api.AutomationTriggerType.EventRegistration
		? ((trigger as Api.IEventRegistrationTrigger).validResponses ?? [])
		: [];
}

const haveSameResponses = (a: Api.EventRegistrationResponseStatus[], b: Api.EventRegistrationResponseStatus[]) =>
	a.length === b.length && a.every(response => b.includes(response));

export function EventAutomationCoverageWarning({
	surveyId,
	automationTemplate,
	validResponses,
	impersonationContext,
	variant,
	allowStart = false,
	styles = [],
}: EventAutomationCoverageWarningProps) {
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [pendingStartContactIds, setPendingStartContactIds] = useState<string[] | null>(null);
	const userSession = useUserSession();
	// The start endpoint needs a user; admin impersonation only carries the account, so act as the automation's creator.
	const startImpersonationContext =
		impersonationContext && !impersonationContext.user?.id && automationTemplate?.creator?.id
			? { ...impersonationContext, user: { id: automationTemplate.creator.id } }
			: impersonationContext;
	const actingUserId = startImpersonationContext?.user?.id ?? userSession.user?.id;
	const startMutation = useStartEventAutomationMutation({
		onSuccess: outcome => {
			const { success, warning } = describeStartOutcome(outcome);
			if (success) {
				toast.success(success);
			}
			if (warning) {
				toast.warning(warning);
			}
			setPendingStartContactIds(null);
			setIsDialogOpen(false);
		},
		onError: error => {
			toast.error(error.systemMessage || 'Couldn’t start this automation.');
			const alreadyEnrolled = error.outcome ? describeStartOutcome(error.outcome).success : undefined;
			if (alreadyEnrolled) {
				toast.info(alreadyEnrolled);
			}
			setPendingStartContactIds(null);
		},
	});
	const coverageQuery = useEventAutomationCoverage({
		surveyId,
		automationTemplateId: automationTemplate?.id,
		validResponses,
		impersonationContext,
	});
	const coverage = coverageQuery.data;

	const hasPublishedVersion = !!automationTemplate?.published;
	const isTriggerChangeUnpublished =
		hasPublishedVersion &&
		(getPublishedEventId(automationTemplate) !== surveyId ||
			(!!validResponses && !haveSameResponses(validResponses, getPublishedValidResponses(automationTemplate))));
	const willRender =
		!!coverage &&
		getCoverageState(coverage, { isEventPast: false, isTriggerChangeUnpublished, hasPublishedVersion }) !== null;

	const surveyQuery = useGetSurveyById<Api.IEventRegistrationSurvey>({
		surveyId: surveyId ?? '',
		surveyType: 'eventRegistration',
		impersonationContext,
		enabled: willRender,
	});

	if (!coverage) {
		if (!coverageQuery.isError) {
			return null;
		}
		return (
			<div
				className={css(
					coverageWarningStyles.container,
					variant === 'inline' && coverageWarningStyles.stacked,
					coverageToneStyles.danger,
					...styles
				)}
				role='alert'
			>
				<div className={css(coverageWarningStyles.copy)}>
					<span className={css(coverageWarningStyles.title)}>
						Couldn&apos;t check whether everyone who registered is enrolled.
					</span>
				</div>
				<Button
					variant='secondary'
					size='sm'
					isLoading={coverageQuery.isFetching}
					onClick={() => coverageQuery.refetch()}
				>
					Try again
				</Button>
			</div>
		);
	}

	// Wait for the event dates so a past event doesn't flash amber before turning grey.
	if (!willRender || surveyQuery.isLoading) {
		return null;
	}

	const eventInformation = surveyQuery.data?.eventInformation;
	const eventEnd = eventInformation?.endTime ?? eventInformation?.startTime;
	const isEventPast = !!eventEnd && moment(eventEnd).isBefore(moment());
	const daysUntilEvent =
		variant === 'banner' && eventInformation?.startTime
			? moment(eventInformation.startTime).startOf('day').diff(moment().startOf('day'), 'days')
			: undefined;

	const state = getCoverageState(coverage, { isEventPast, isTriggerChangeUnpublished, hasPublishedVersion });
	if (!state) {
		return null;
	}
	const copy = getCoverageCopy(state, coverage, variant, daysUntilEvent);
	const eventReportPath = surveyQuery.data?.isPaidEvent ? `paid/${surveyId}` : surveyId;
	const reportUrl = impersonationContext?.account?.id
		? undefined
		: `/#/reporting/surveys/event-reports/${eventReportPath}`;
	const startableContactIds = getStartableContactIds(coverage);
	// The server only starts before the event begins (not before it ends), so unknown dates can't be offered either.
	const isBeforeEventStart = !!eventInformation?.startTime && moment(eventInformation.startTime).isAfter(moment());
	// The server refuses unless the event links back to this automation, which it stops doing once disabled or re-pointed.
	const canStart =
		allowStart &&
		state === 'missed' &&
		isBeforeEventStart &&
		!!automationTemplate?.id &&
		coverage.surveyLinkedAutomationTemplateId === automationTemplate.id &&
		startableContactIds.length > 0;
	const includesOtherUsersContacts = (contactIds: string[]) =>
		coverage.registrants.some(
			registrant => contactIds.includes(registrant.contact?.id) && registrant.contact?.ownerId !== actingUserId
		);

	return (
		<>
			<div
				className={css(
					coverageWarningStyles.container,
					variant === 'inline' && coverageWarningStyles.stacked,
					coverageToneStyles[copy.tone],
					...styles
				)}
				role={copy.tone === 'danger' ? 'alert' : 'status'}
			>
				<div className={css(coverageWarningStyles.copy)}>
					<span className={css(coverageWarningStyles.title)}>{copy.title}</span>
					<span className={css(coverageWarningStyles.body)}>{copy.body}</span>
				</div>
				{copy.actionLabel ? (
					<Button
						variant={variant === 'banner' ? 'secondary' : 'link'}
						size='sm'
						styles={variant === 'inline' ? [coverageWarningStyles.inlineAction] : []}
						onClick={() => setIsDialogOpen(true)}
					>
						{copy.actionLabel}
					</Button>
				) : null}
			</div>
			<EventAutomationCoverageDialog
				open={isDialogOpen}
				onOpenChange={setIsDialogOpen}
				coverage={coverage}
				eventName={surveyQuery.data?.name}
				isEventPast={isEventPast}
				isRetargeted={state === 'retargeted'}
				reportUrl={reportUrl}
				startableContactIds={canStart ? startableContactIds : undefined}
				onStart={setPendingStartContactIds}
			/>
			<StartEventAutomationDialog
				open={!!pendingStartContactIds}
				onOpenChange={open => !open && setPendingStartContactIds(null)}
				count={pendingStartContactIds?.length ?? 0}
				includesOtherUsersContacts={!!pendingStartContactIds && includesOtherUsersContacts(pendingStartContactIds)}
				isStarting={startMutation.isPending}
				onConfirm={() =>
					pendingStartContactIds &&
					surveyId &&
					startMutation.mutate({
						surveyId,
						contactIds: pendingStartContactIds,
						impersonationContext: startImpersonationContext,
					})
				}
			/>
		</>
	);
}
